/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback } from 'react';

const ParamPanel = ({
  model,
  params,
  onParamChange,
  testing,
  testResult,
  onTest,
  category,
  onEndpointChange,
  onTabChange,
}) => {
  const [apiKey, setApiKey] = useState(() => {
    try {
      return sessionStorage.getItem('playground_api_key') || '';
    } catch {
      return '';
    }
  });
  const handleApiKeyChange = (e) => {
    const val = e.target.value;
    setApiKey(val);
    try {
      sessionStorage.setItem('playground_api_key', val);
    } catch {}
  };
  const [activeTab, setActiveTab] = useState(null);
  const [currentEndpoint, setCurrentEndpoint] = useState(null);

  // Vertical resize between param-content and param-test-panel
  const [testPanelHeight, setTestPanelHeight] = useState(220);
  const vResizeDrag = useRef({ active: false, startY: 0, startHeight: 220 });

  const onVMouseMove = useCallback((e) => {
    if (!vResizeDrag.current.active) return;
    const delta = vResizeDrag.current.startY - e.clientY;
    const next = Math.max(
      120,
      Math.min(600, vResizeDrag.current.startHeight + delta)
    );
    setTestPanelHeight(next);
  }, []);

  const onVMouseUp = useCallback(() => {
    if (!vResizeDrag.current.active) return;
    vResizeDrag.current.active = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', onVMouseMove);
    document.addEventListener('mouseup', onVMouseUp);
    return () => {
      document.removeEventListener('mousemove', onVMouseMove);
      document.removeEventListener('mouseup', onVMouseUp);
    };
  }, [onVMouseMove, onVMouseUp]);

  const handleVResizeMouseDown = (e) => {
    e.preventDefault();
    vResizeDrag.current = {
      active: true,
      startY: e.clientY,
      startHeight: testPanelHeight,
    };
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  };

  // 根据模型标签过滤可用的tabs
  const getFilteredTabs = () => {
    if (!category || !category.tabs) {
      return [];
    }

    // 模型没有标签时，显示所有tabs
    if (!model || !model.tags) {
      return category.tabs;
    }

    // 过滤出与模型标签有交集的tabs
    return category.tabs.filter((tab) => {
      if (!tab.tags || tab.tags.length === 0) {
        return true; // 没有标签限制的tab对所有模型可见
      }
      // 检查tab的tags和model的tags是否有交集
      return tab.tags.some((tag) => model.tags.includes(tag));
    });
  };

  // 初始化activeTab和endpoint
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const filteredTabs = getFilteredTabs();
    if (filteredTabs.length > 0) {
      setActiveTab(filteredTabs[0].id);
      setCurrentEndpoint(filteredTabs[0].endpoint);
      if (onEndpointChange) {
        onEndpointChange(filteredTabs[0].endpoint);
      }
      if (onTabChange) {
        onTabChange(filteredTabs[0].id);
      }
    } else if (model) {
      setCurrentEndpoint(model.endpoint);
      if (onEndpointChange) {
        onEndpointChange(model.endpoint);
      }
      if (onTabChange) {
        onTabChange(null);
      }
    }
  }, [model, category]);

  // 当activeTab改变时更新endpoint
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (activeTab && category && category.tabs) {
      const tab = category.tabs.find((t) => t.id === activeTab);
      if (tab && tab.endpoint) {
        setCurrentEndpoint(tab.endpoint);
        if (onEndpointChange) {
          onEndpointChange(tab.endpoint);
        }
      }
    }
    if (onTabChange) {
      onTabChange(activeTab);
    }
  }, [activeTab, category]);

  if (!model) {
    return (
      <div className='playground-param-panel'>
        <div className='param-header'>
          <h2 className='param-model-name'>请选择模型</h2>
        </div>
      </div>
    );
  }

  const renderParamInput = (param) => {
    const value = params[param.name];
    const hasOptions = Array.isArray(param.options) && param.options.length > 0;

    if (param.type === 'multi_select') {
      const currentMultiValue =
        Array.isArray(value) && value.length > 0 ? String(value[0]) : '';
      return (
        <select
          className='param-select'
          value={currentMultiValue}
          onChange={(e) =>
            onParamChange(param.name, e.target.value ? [e.target.value] : [])
          }
        >
          <option value=''>不选择</option>
          {(param.options || []).map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (hasOptions || param.type === 'select') {
      // Keep original option value types (boolean/number/string) instead of always using string.
      const currentSelectValue =
        value !== undefined && value !== null
          ? String(value)
          : String(param.default ?? '');
      return (
        <select
          className='param-select'
          value={currentSelectValue}
          onChange={(e) => {
            const selectedOption = (param.options || []).find(
              (opt) => String(opt.value) === e.target.value
            );
            onParamChange(
              param.name,
              selectedOption ? selectedOption.value : e.target.value
            );
          }}
        >
          {(param.options || []).map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    switch (param.type) {
      case 'slider':
        return (
          <div className='param-number'>
            <input
              type='range'
              className='param-slider'
              min={param.min}
              max={param.max}
              step={param.step}
              value={value || param.default}
              onChange={(e) =>
                onParamChange(param.name, parseFloat(e.target.value))
              }
            />
            <input
              type='number'
              className='param-input param-number-input'
              min={param.min}
              max={param.max}
              step={param.step}
              value={value || param.default}
              onChange={(e) =>
                onParamChange(param.name, parseFloat(e.target.value))
              }
            />
          </div>
        );

      case 'number':
        return (
          <input
            type='number'
            className='param-input'
            min={param.min}
            max={param.max}
            value={value || param.default}
            onChange={(e) =>
              onParamChange(param.name, parseFloat(e.target.value))
            }
          />
        );

      case 'textarea':
        return (
          <textarea
            className='param-textarea'
            value={
              typeof value === 'string'
                ? value
                : value !== undefined && value !== null
                ? JSON.stringify(value, null, 2)
                : ''
            }
            placeholder={param.placeholder}
            onChange={(e) => onParamChange(param.name, e.target.value)}
          />
        );

      case 'image_urls':
      case 'file_urls':
        return (
          <textarea
            className='param-textarea'
            value={
              typeof value === 'string'
                ? value
                : value !== undefined && value !== null
                ? JSON.stringify(value, null, 2)
                : ''
            }
            placeholder={
              param.placeholder ||
              '["https://example.com/file-1", "https://example.com/file-2"]'
            }
            onChange={(e) => onParamChange(param.name, e.target.value)}
          />
        );

      default:
        return (
          <input
            type='text'
            className='param-input'
            value={value || ''}
            placeholder={param.placeholder}
            onChange={(e) => onParamChange(param.name, e.target.value)}
          />
        );
    }
  };

  const handleTest = () => {
    if (!apiKey) {
      alert('请输入 API Key');
      return;
    }
    onTest(apiKey, currentEndpoint);
  };

  // 判断当前tab类型
  const getCurrentTabType = () => {
    if (!activeTab || !category || !category.tabs) return null;
    const tab = category.tabs.find((t) => t.id === activeTab);
    if (!tab) return null;

    // 主体管理 tabs
    if (tab.id === 'subject-create') return 'subject-create';
    if (tab.id === 'subject-list') return 'subject-list';
    if (tab.id === 'subject-query') return 'query';

    // 素材管理 tabs
    if (tab.id === 'asset-group-create') return 'asset-group-create';
    if (tab.id === 'asset-create') return 'asset-create';
    if (tab.id === 'asset-group-get') return 'asset-group-get';
    if (tab.id === 'asset-get') return 'asset-get';
    if (tab.id === 'asset-group-list') return 'asset-group-list';
    if (tab.id === 'asset-group-update') return 'asset-group-update';
    if (tab.id === 'asset-list') return 'asset-list';
    if (tab.id === 'asset-update') return 'asset-update';

    // 字幕擦除 tabs
    if (tab.id === 'subtitle-erase') return 'subtitle-erase';
    if (tab.id === 'subtitle-erase-query') return 'query';

    if (tab.id === 'query' || tab.name.includes('查询')) return 'query';
    if (tab.id === 'generation' || tab.name.includes('生成')) {
      // 根据category判断生成类型
      if (category.id === 'video') return 'video-generation';
      if (category.id === 'music') return 'music-generation';
      return 'image-generation';
    }
    if (
      tab.id === 'edit' ||
      tab.name.includes('修改') ||
      tab.name.includes('编辑')
    )
      return 'edit';
    return null;
  };

  const getParamDefinition = (paramName) => {
    if (!model || !model.extra_params) {
      return null;
    }
    return model.extra_params.find((param) => param.name === paramName) || null;
  };

  const renderModelField = ({
    required = false,
    defaultDescription = '模型ID，默认为当前选择的模型',
  } = {}) => {
    const modelParam = getParamDefinition('model');
    const isRequired = required || modelParam?.required;
    const description = modelParam?.description || defaultDescription;
    return (
      <div className='param-form-item'>
        <label className='param-label'>
          model
          {isRequired ? (
            <span className='param-label-required'> *</span>
          ) : (
            <span className='param-label-optional'> (可选)</span>
          )}
        </label>
        {modelParam ? (
          renderParamInput(modelParam)
        ) : (
          <input
            type='text'
            className='param-input'
            value={params.model || model.id}
            onChange={(e) => onParamChange('model', e.target.value)}
            placeholder='模型ID'
          />
        )}
        <div className='param-description'>{description}</div>
      </div>
    );
  };

  // 渲染图片生成表单（ImageRequest结构）
  const renderImageGenerationForm = () => {
    return (
      <>
        {renderModelField({ required: false })}

        <div className='param-form-item'>
          <label className='param-label'>
            prompt
            <span className='param-label-required'> *</span>
          </label>
          <textarea
            className='param-textarea'
            value={params.prompt || ''}
            onChange={(e) => onParamChange('prompt', e.target.value)}
            placeholder='描述你想生成的图片'
          />
          <div className='param-description'>图片生成提示词（必填）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            size
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.size || ''}
            onChange={(e) => onParamChange('size', e.target.value)}
            placeholder='例如: 1024x1024'
          />
          <div className='param-description'>图片尺寸</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            n<span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.n || ''}
            onChange={(e) =>
              onParamChange('n', e.target.value ? parseInt(e.target.value) : '')
            }
            placeholder='例如: 1'
          />
          <div className='param-description'>图片数量</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            images
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.images === 'string'
                ? params.images
                : params.images
                ? JSON.stringify(params.images, null, 2)
                : '[]'
            }
            onChange={(e) => onParamChange('images', e.target.value)}
            placeholder='["图片URL1", "图片URL2"]'
          />
          <div className='param-description'>参考图片列表，JSON数组格式</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            watermark
            <span className='param-label-optional'> (可选)</span>
          </label>
          <select
            className='param-select'
            value={
              params.watermark !== undefined
                ? params.watermark.toString()
                : 'false'
            }
            onChange={(e) =>
              onParamChange('watermark', e.target.value === 'true')
            }
          >
            <option value='false'>否</option>
            <option value='true'>是</option>
          </select>
          <div className='param-description'>是否添加水印（默认：否）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外参数，JSON格式</div>
        </div>
      </>
    );
  };

  // 渲染视频生成表单（使用模型的extra_params动态生成）
  const renderVideoGenerationForm = () => {
    const extraParams = model.extra_params || [];

    // 合并base_params中的字段到extra_params（如果extra_params中没有定义）
    const mergedParams = [...extraParams];
    if (model.base_params) {
      Object.keys(model.base_params).forEach((key) => {
        if (!mergedParams.find((p) => p.name === key)) {
          const defaultValue = model.base_params[key];
          const detectedType =
            typeof defaultValue === 'number'
              ? 'number'
              : typeof defaultValue === 'boolean'
              ? 'text'
              : typeof defaultValue === 'object' && defaultValue !== null
              ? 'textarea'
              : 'text';
          mergedParams.unshift({
            name: key,
            type: detectedType,
            optional: true,
            default: defaultValue,
          });
        }
      });
    }

    return (
      <>
        {mergedParams.map((param) => {
          // 特殊处理 model 字段
          if (param.name === 'model') {
            return (
              <div key={param.name} className='param-form-item'>
                <label className='param-label'>
                  {param.name}
                  {(param.required ?? true) && (
                    <span className='param-label-required'> *</span>
                  )}
                </label>
                {(Array.isArray(param.options) && param.options.length > 0) ||
                param.type === 'select' ? (
                  renderParamInput(param)
                ) : (
                  <input
                    type='text'
                    className='param-input'
                    value={params.model || model.id}
                    onChange={(e) => onParamChange('model', e.target.value)}
                    placeholder={param.placeholder || '模型ID'}
                  />
                )}
                <div className='param-description'>
                  {param.description || '模型ID'}
                </div>
              </div>
            );
          }
          return (
            <div key={param.name} className='param-form-item'>
              <label className='param-label'>
                {param.name}
                {param.required && (
                  <span className='param-label-required'> *</span>
                )}
                {param.optional && (
                  <span className='param-label-optional'> (可选)</span>
                )}
              </label>
              {renderParamInput(param)}
              {param.description && (
                <div className='param-description'>{param.description}</div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  // 渲染主体创建表单
  const renderSubjectCreateForm = () => {
    const refType = params.reference_type || 'image_refer';
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            element_name
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.element_name || ''}
            maxLength={20}
            onChange={(e) => onParamChange('element_name', e.target.value)}
            placeholder='主体名称'
          />
          <div className='param-description'>
            自定义主体的名称（最多 20 字符）
          </div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            element_description
            <span className='param-label-required'> *</span>
          </label>
          <textarea
            className='param-textarea'
            value={params.element_description || ''}
            maxLength={100}
            onChange={(e) =>
              onParamChange('element_description', e.target.value)
            }
            placeholder='描述主体的外观特征...'
          />
          <div className='param-description'>
            主体的详细描述（最多 100 字符）
          </div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            reference_type
            <span className='param-label-required'> *</span>
          </label>
          <select
            className='param-select'
            value={refType}
            onChange={(e) => onParamChange('reference_type', e.target.value)}
          >
            <option value='image_refer'>图片参考 (image_refer)</option>
            <option value='video_refer'>视频参考 (video_refer)</option>
          </select>
          <div className='param-description'>参考方式：图片参考或视频参考</div>
        </div>

        {refType === 'image_refer' && (
          <>
            <div className='param-form-item'>
              <label className='param-label'>
                frontal_image
                <span className='param-label-required'> *</span>
              </label>
              <input
                type='text'
                className='param-input'
                value={params.frontal_image || ''}
                onChange={(e) => onParamChange('frontal_image', e.target.value)}
                placeholder='https://example.com/front.jpg'
              />
              <div className='param-description'>正面参考图 URL（必填）</div>
            </div>

            <div className='param-form-item'>
              <label className='param-label'>
                refer_images
                <span className='param-label-required'> *</span>
              </label>
              <textarea
                className='param-textarea'
                value={
                  typeof params.refer_images === 'string'
                    ? params.refer_images
                    : params.refer_images
                    ? JSON.stringify(params.refer_images, null, 2)
                    : ''
                }
                onChange={(e) => onParamChange('refer_images', e.target.value)}
                placeholder='[{"image_url": "https://example.com/side.jpg"}]'
              />
              <div className='param-description'>
                其他角度参考图列表，JSON 数组格式，1-3 张
              </div>
            </div>
          </>
        )}

        {refType === 'video_refer' && (
          <div className='param-form-item'>
            <label className='param-label'>
              video_url
              <span className='param-label-required'> *</span>
            </label>
            <input
              type='text'
              className='param-input'
              value={params.video_url || ''}
              onChange={(e) => onParamChange('video_url', e.target.value)}
              placeholder='https://example.com/reference.mp4'
            />
            <div className='param-description'>
              参考视频 URL（MP4/MOV，1080P，3-8秒，16:9 或 9:16，≤200MB）
            </div>
          </div>
        )}

        <div className='param-form-item'>
          <label className='param-label'>
            element_voice_id
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.element_voice_id || ''}
            onChange={(e) => onParamChange('element_voice_id', e.target.value)}
            placeholder='音色ID'
          />
          <div className='param-description'>绑定的音色 ID</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            tag_list
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.tag_list === 'string'
                ? params.tag_list
                : params.tag_list
                ? JSON.stringify(params.tag_list, null, 2)
                : ''
            }
            onChange={(e) => onParamChange('tag_list', e.target.value)}
            placeholder='[{"tag_id": "o_101"}]'
          />
          <div className='param-description'>
            主体标签列表，JSON 数组。可选 tag_id: o_101(人物) o_102(宠物)
            o_103(服饰) o_104(建筑) o_105(食物) o_106(交通工具) o_107(Logo)
            o_108(其他)
          </div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染创建素材组合表单
  const renderAssetGroupCreateForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            name
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.name || ''}
            maxLength={64}
            onChange={(e) => onParamChange('name', e.target.value)}
            placeholder='素材组合名称'
          />
          <div className='param-description'>素材组合名称（最多 64 字符）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            description
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={params.description || ''}
            maxLength={300}
            onChange={(e) => onParamChange('description', e.target.value)}
            placeholder='素材组合描述...'
          />
          <div className='param-description'>素材组合描述（最多 300 字符）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            group_type
            <span className='param-label-optional'> (可选)</span>
          </label>
          <select
            className='param-select'
            value={params.group_type || 'AIGC'}
            onChange={(e) => onParamChange('group_type', e.target.value)}
          >
            <option value='AIGC'>AIGC</option>
          </select>
          <div className='param-description'>素材组合类型</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染创建素材表单
  const renderAssetCreateForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            group_id
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.group_id || ''}
            onChange={(e) => onParamChange('group_id', e.target.value)}
            placeholder='素材组合 ID'
          />
          <div className='param-description'>素材组合 ID</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            url
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.url || ''}
            onChange={(e) => onParamChange('url', e.target.value)}
            placeholder='https://example.com/asset.png'
          />
          <div className='param-description'>素材公共可访问地址</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            asset_type
            <span className='param-label-required'> *</span>
          </label>
          <select
            className='param-select'
            value={params.asset_type || 'Image'}
            onChange={(e) => onParamChange('asset_type', e.target.value)}
          >
            <option value='Image'>Image</option>
            <option value='Audio'>Audio</option>
            <option value='Video'>Video</option>
          </select>
          <div className='param-description'>素材类型</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.name || ''}
            maxLength={64}
            onChange={(e) => onParamChange('name', e.target.value)}
            placeholder='素材名称'
          />
          <div className='param-description'>素材名称（最多 64 字符）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染查询素材组合表单
  const renderAssetGroupGetForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            id
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.id || ''}
            onChange={(e) => onParamChange('id', e.target.value)}
            placeholder='素材组合 ID'
          />
          <div className='param-description'>素材组合 ID</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染查询素材表单
  const renderAssetGetForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            id
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.id || ''}
            onChange={(e) => onParamChange('id', e.target.value)}
            placeholder='素材 ID'
          />
          <div className='param-description'>素材 ID</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染素材组合列表表单
  const renderAssetGroupListForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            filters
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.filters === 'string'
                ? params.filters
                : params.filters
                ? JSON.stringify(params.filters, null, 2)
                : ''
            }
            onChange={(e) => onParamChange('filters', e.target.value)}
            placeholder='{"name":"","group_ids":[],"group_type":"AIGC"}'
          />
          <div className='param-description'>
            筛选条件，JSON 格式。可选字段: name, group_ids, group_type
          </div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            page_number
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.page_number || ''}
            onChange={(e) =>
              onParamChange(
                'page_number',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            placeholder='1'
            min='1'
          />
          <div className='param-description'>页码</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            page_size
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.page_size || ''}
            onChange={(e) =>
              onParamChange(
                'page_size',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            placeholder='10'
            min='1'
            max='100'
          />
          <div className='param-description'>每页条数，上限 100</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            sort_by
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.sort_by || ''}
            onChange={(e) => onParamChange('sort_by', e.target.value)}
            placeholder='CreateTime'
          />
          <div className='param-description'>排序字段</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            sort_order
            <span className='param-label-optional'> (可选)</span>
          </label>
          <select
            className='param-select'
            value={params.sort_order || ''}
            onChange={(e) => onParamChange('sort_order', e.target.value)}
          >
            <option value=''>默认</option>
            <option value='asc'>升序 (asc)</option>
            <option value='desc'>降序 (desc)</option>
          </select>
          <div className='param-description'>排序方向</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染素材列表表单
  const renderAssetListForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            filters
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.filters === 'string'
                ? params.filters
                : params.filters
                ? JSON.stringify(params.filters, null, 2)
                : ''
            }
            onChange={(e) => onParamChange('filters', e.target.value)}
            placeholder='{"group_ids":[],"group_type":"AIGC","statuses":["Active"],"name":""}'
          />
          <div className='param-description'>
            筛选条件，JSON 格式。可选字段: group_ids, group_type(必填, AIGC),
            statuses(Active/Processing/Failed), name
          </div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            page_number
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.page_number || ''}
            onChange={(e) =>
              onParamChange(
                'page_number',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            placeholder='1'
            min='1'
          />
          <div className='param-description'>页码，从 1 开始</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            page_size
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.page_size || ''}
            onChange={(e) =>
              onParamChange(
                'page_size',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            placeholder='10'
            min='1'
            max='100'
          />
          <div className='param-description'>每页条数，上限 100</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            sort_by
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.sort_by || ''}
            onChange={(e) => onParamChange('sort_by', e.target.value)}
            placeholder='CreateTime'
          />
          <div className='param-description'>
            排序字段: CreateTime, UpdateTime, GroupId
          </div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            sort_order
            <span className='param-label-optional'> (可选)</span>
          </label>
          <select
            className='param-select'
            value={params.sort_order || ''}
            onChange={(e) => onParamChange('sort_order', e.target.value)}
          >
            <option value=''>默认</option>
            <option value='Asc'>升序 (Asc)</option>
            <option value='Desc'>降序 (Desc)</option>
          </select>
          <div className='param-description'>排序方向</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染更新素材表单
  const renderAssetUpdateForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            id
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.id || ''}
            onChange={(e) => onParamChange('id', e.target.value)}
            placeholder='素材 ID'
          />
          <div className='param-description'>素材 ID</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.name || ''}
            maxLength={64}
            onChange={(e) => onParamChange('name', e.target.value)}
            placeholder='新的素材名称'
          />
          <div className='param-description'>素材名称（最多 64 字符）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染更新素材组合表单
  const renderAssetGroupUpdateForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            id
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.id || ''}
            onChange={(e) => onParamChange('id', e.target.value)}
            placeholder='素材组合 ID'
          />
          <div className='param-description'>素材组合 ID</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.name || ''}
            maxLength={64}
            onChange={(e) => onParamChange('name', e.target.value)}
            placeholder='新的素材组合名称'
          />
          <div className='param-description'>素材组合名称（最多 64 字符）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            description
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={params.description || ''}
            maxLength={300}
            onChange={(e) => onParamChange('description', e.target.value)}
            placeholder='新的素材组合描述...'
          />
          <div className='param-description'>素材组合描述（最多 300 字符）</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            project_name
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.project_name || ''}
            onChange={(e) => onParamChange('project_name', e.target.value)}
            placeholder='default'
          />
          <div className='param-description'>项目名称，默认 default</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外透传参数，JSON 格式</div>
        </div>
      </>
    );
  };

  // 渲染主体列表查询表单
  const renderSubjectListForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            type
            <span className='param-label-optional'> (可选)</span>
          </label>
          <select
            className='param-select'
            value={params.type || 'Custom-Elements'}
            onChange={(e) => onParamChange('type', e.target.value)}
          >
            <option value='Custom-Elements'>
              自定义主体 (Custom-Elements)
            </option>
            <option value='Presets-Elements'>
              预置主体 (Presets-Elements)
            </option>
          </select>
          <div className='param-description'>主体类型</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            pageNum
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.pageNum || 1}
            onChange={(e) =>
              onParamChange('pageNum', parseInt(e.target.value) || 1)
            }
            placeholder='1'
            min='1'
          />
          <div className='param-description'>页码，默认 1</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            pageSize
            <span className='param-label-optional'> (可选)</span>
          </label>
          <input
            type='number'
            className='param-input'
            value={params.pageSize || 30}
            onChange={(e) =>
              onParamChange('pageSize', parseInt(e.target.value) || 30)
            }
            placeholder='30'
            min='1'
            max='100'
          />
          <div className='param-description'>每页条数，默认 30</div>
        </div>
      </>
    );
  };

  // 渲染字幕擦除提交表单
  const renderSubtitleEraseForm = () => {
    // 字幕擦除使用固定模型名
    const subtitleModel = params.model || 'Doubao-subtitle-erase';
    if (!params.model) {
      onParamChange('model', 'Doubao-subtitle-erase');
    }
    return (
      <>
        <div className='param-form-item'>
          <label className='param-label'>
            model
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={subtitleModel}
            onChange={(e) => onParamChange('model', e.target.value)}
            placeholder='模型ID'
          />
          <div className='param-description'>固定为 Doubao-subtitle-erase</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            video_url
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.video_url || ''}
            onChange={(e) => onParamChange('video_url', e.target.value)}
            placeholder='https://example.com/your-video.mp4'
          />
          <div className='param-description'>
            需要擦除字幕的视频地址（公网可访问），免费功能
          </div>
        </div>
      </>
    );
  };

  // 渲染查询表单（固定结构）
  const renderQueryForm = () => {
    return (
      <>
        {renderModelField({ required: true })}

        <div className='param-form-item'>
          <label className='param-label'>
            task_id
            <span className='param-label-required'> *</span>
          </label>
          <input
            type='text'
            className='param-input'
            value={params.task_id || ''}
            onChange={(e) => onParamChange('task_id', e.target.value)}
            placeholder='任务ID'
          />
          <div className='param-description'>生成任务返回的task_id</div>
        </div>

        <div className='param-form-item'>
          <label className='param-label'>
            byPass
            <span className='param-label-optional'> (可选)</span>
          </label>
          <textarea
            className='param-textarea'
            value={
              typeof params.byPass === 'string'
                ? params.byPass
                : params.byPass
                ? JSON.stringify(params.byPass, null, 2)
                : '{}'
            }
            onChange={(e) => onParamChange('byPass', e.target.value)}
            placeholder='{}'
          />
          <div className='param-description'>额外参数，JSON格式</div>
        </div>
      </>
    );
  };

  return (
    <div className='playground-param-panel'>
      <div className='param-header'>
        <h2 className='param-model-name'>{model.name}</h2>
        <p className='param-model-desc'>{model.description}</p>
        <div className='param-model-tags'>
          <span className='param-tag'>{model.provider}</span>
          {model.pricing && (
            <span className='param-tag'>
              ${model.pricing.input} / ${model.pricing.output} per{' '}
              {model.pricing.unit}
            </span>
          )}
        </div>
      </div>

      {/* Tab切换 */}
      {(() => {
        const filteredTabs = getFilteredTabs();
        return (
          filteredTabs.length > 1 && (
            <div className='param-tabs'>
              {filteredTabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`param-tab ${
                    activeTab === tab.id ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.name}
                </div>
              ))}
            </div>
          )
        );
      })()}

      <div className='param-content'>
        {(() => {
          const tabType = getCurrentTabType();
          if (tabType === 'subject-create') {
            return renderSubjectCreateForm();
          } else if (tabType === 'subject-list') {
            return renderSubjectListForm();
          } else if (tabType === 'asset-group-create') {
            return renderAssetGroupCreateForm();
          } else if (tabType === 'asset-create') {
            return renderAssetCreateForm();
          } else if (tabType === 'asset-group-get') {
            return renderAssetGroupGetForm();
          } else if (tabType === 'asset-get') {
            return renderAssetGetForm();
          } else if (tabType === 'asset-group-list') {
            return renderAssetGroupListForm();
          } else if (tabType === 'asset-group-update') {
            return renderAssetGroupUpdateForm();
          } else if (tabType === 'asset-list') {
            return renderAssetListForm();
          } else if (tabType === 'asset-update') {
            return renderAssetUpdateForm();
          } else if (tabType === 'subtitle-erase') {
            return renderSubtitleEraseForm();
          } else if (tabType === 'query') {
            return renderQueryForm();
          } else if (tabType === 'image-generation') {
            return renderImageGenerationForm();
          } else if (tabType === 'video-generation') {
            return renderVideoGenerationForm();
          } else if (tabType === 'music-generation') {
            // Music generation uses dynamic param rendering from model.extra_params.
            return renderVideoGenerationForm();
          } else if (tabType === 'edit') {
            return renderImageGenerationForm(); // 编辑也使用相同的ImageRequest结构
          } else {
            // 默认显示模型的extra_params
            return (
              model.extra_params &&
              model.extra_params.map((param) => (
                <div key={param.name} className='param-form-item'>
                  <label className='param-label'>
                    {param.name}
                    {param.required && (
                      <span className='param-label-required'> *</span>
                    )}
                    {param.optional && (
                      <span className='param-label-optional'> (可选)</span>
                    )}
                  </label>
                  {renderParamInput(param)}
                  {param.description && (
                    <div className='param-description'>{param.description}</div>
                  )}
                </div>
              ))
            );
          }
        })()}
      </div>

      <div
        className='param-test-resize-handle'
        onMouseDown={handleVResizeMouseDown}
      />
      <div
        className='param-test-panel'
        style={{ height: testPanelHeight, minHeight: 120 }}
      >
        <div className='test-api-key'>
          <label className='test-api-key-label'>API Key</label>
          <input
            type='password'
            className='test-api-key-input'
            value={apiKey}
            onChange={handleApiKeyChange}
            placeholder='输入你的 API Key'
          />
          <div className='test-api-key-hint'>
            你的 API Key 仅保存在当前会话中，关闭标签页后自动清除
          </div>
        </div>

        <button
          className='test-button'
          onClick={handleTest}
          disabled={testing || !apiKey}
        >
          {testing ? '测试中...' : '运行测试'}
        </button>

        {testResult && (
          <div className='test-result'>
            <div className='test-result-title'>测试结果</div>
            {testResult.error ? (
              <div className='test-result-content test-result-error'>
                {testResult.error}
              </div>
            ) : (
              <>
                <div className='test-result-status'>
                  状态码: {testResult.status_code}
                </div>
                <div className='test-result-content'>
                  <pre>{JSON.stringify(testResult.body, null, 2)}</pre>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParamPanel;
